import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Plus, Edit2, Trash2, X, Package, ShoppingCart, Users, DollarSign, BookOpen, FileText, Mail, Tag, Inbox, Settings as SettingsIcon, Feather, Eye, Copy, Send, PenLine, CheckCircle2, AlertCircle } from "lucide-react";
import api, { formatPrice, resolveImg } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import ImageUpload from "../components/ImageUpload";
import MultiImageUpload from "../components/MultiImageUpload";
import OrderFulfillment, { StepChip } from "../components/admin/OrderFulfillment";
import { orderStep, isOverdue, isWaitingOnSupplier, daysSincePaid, NOT_SHIPPED_STEPS } from "../lib/shipping";
import { stockOf, LOW_STOCK } from "../lib/stock";
import OrderItemsPanel from "../components/admin/OrderItemsPanel";
import { mtDate, mtDateTime, inThisWeek, inThisMonth } from "../lib/dates";

const TABS = [
  { id: "dashboard", name: "Dashboard" },
  { id: "orders", name: "Orders" },
  { id: "messages", name: "Messages" },
  { id: "products", name: "Products" },
  { id: "recipes", name: "Recipes" },
  { id: "blog", name: "Blog" },
  { id: "coupons", name: "Coupons" },
  { id: "subscribers", name: "Subscribers" },
  { id: "settings", name: "Settings" },
];

export default function Admin() {
  const { user, loading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "dashboard";
  const [tab, setTab] = useState(TABS.find((t) => t.id === initialTab) ? initialTab : "dashboard");
  const [badges, setBadges] = useState({ unseen_orders: 0, unread_messages: 0 });

  // Keep tab in URL so email links (?tab=orders&open=...) work.
  useEffect(() => {
    if (searchParams.get("tab") !== tab) {
      const next = new URLSearchParams(searchParams);
      next.set("tab", tab);
      // Preserve `open=` only when staying in the tab it belongs to.
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const refreshBadges = () => {
    api.get("/admin/stats").then((r) => {
      setBadges({
        unseen_orders: r.data.unseen_orders || 0,
        unread_messages: r.data.unread_messages || 0,
      });
    }).catch(() => {});
  };
  useEffect(() => { refreshBadges(); }, [tab]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" />;
  if (user.role !== "admin") return <Navigate to="/" />;

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12" data-testid="admin-page">
      <p className="eyebrow mb-3">Admin</p>
      <h1 className="font-serif text-5xl text-[var(--ink-forest)] tracking-tight">Shop control</h1>
      <p className="text-[var(--muted-fg)] mt-2">Welcome, {user.name}</p>

      <div className="border-b border-[var(--border)] mt-10 flex gap-1 overflow-x-auto">
        {TABS.map((t) => {
          const badge =
            t.id === "orders" ? badges.unseen_orders :
            t.id === "messages" ? badges.unread_messages : 0;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 sm:px-5 py-3 text-sm font-medium border-b-2 transition-colors inline-flex items-center gap-2 whitespace-nowrap ${
                tab === t.id
                  ? "border-[var(--terracotta)] text-[var(--terracotta)]"
                  : "border-transparent text-[var(--muted-fg)] hover:text-[var(--ink-forest)]"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              {t.name}
              {badge > 0 && (
                <span
                  className="bg-[var(--terracotta)] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none"
                  data-testid={`admin-tab-${t.id}-badge`}
                >
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-8">
        {tab === "dashboard" && <Dashboard onJumpTab={setTab} />}
        {tab === "products" && <Products />}
        {tab === "recipes" && <Recipes />}
        {tab === "blog" && <Blog />}
        {tab === "coupons" && <Coupons />}
        {tab === "orders" && <Orders onBadgesChanged={refreshBadges} />}
        {tab === "messages" && <Messages onBadgesChanged={refreshBadges} />}
        {tab === "subscribers" && <Subscribers />}
        {tab === "settings" && <SettingsPanel />}
      </div>
    </div>
  );
}

function Dashboard({ onJumpTab }) {
  const [stats, setStats] = useState(null);
  const [recovered, setRecovered] = useState([]);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/orders/recovered").then((r) => setRecovered(r.data || [])).catch(() => {});
  }, []);
  if (!stats) return <div className="h-32 bg-[var(--muted)] rounded-2xl animate-pulse" />;

  const unseenRecovered = recovered.filter((o) => !o.admin_seen);

  return (
    <div className="space-y-7" data-testid="admin-dashboard">
      {unseenRecovered.length > 0 && (
        <div
          className="bg-[var(--bg-surface)] border-2 border-[var(--terracotta)]/35 rounded-2xl p-6 shadow-sm"
          data-testid="recovered-orders-banner"
        >
          <div className="flex items-start gap-4">
            <div className="shrink-0 w-11 h-11 rounded-full bg-[var(--terracotta)]/10 flex items-center justify-center">
              <Feather className="w-5 h-5 text-[var(--terracotta)]" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <p className="eyebrow text-[var(--terracotta)] mb-1">A gentle heads-up</p>
              <h3 className="font-serif text-2xl text-[var(--ink-forest)] leading-tight">
                {unseenRecovered.length} order{unseenRecovered.length === 1 ? "" : "s"} recovered —
                <span className="italic text-[var(--terracotta)]">
                  {" "}{unseenRecovered.map((o) => o.customer_name).join(" & ")}
                </span>
              </h3>
              <p className="text-sm text-[var(--muted-fg)] mt-2 leading-relaxed max-w-2xl">
                These came in and slipped past us. Open each one, review what they ordered, and — when you're ready — send them a warm apology-and-status note.
                You can review the draft first, edit it, or send it from your own email so it feels truly personal.
              </p>
              <button
                onClick={() => onJumpTab && onJumpTab("orders")}
                className="mt-4 bg-[var(--terracotta)] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[var(--terracotta-deep)] transition-colors inline-flex items-center gap-2"
                data-testid="recovered-banner-cta"
              >
                Open these orders <span>→</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard icon={DollarSign} label="Revenue" value={formatPrice(stats.total_revenue)} />
        <StatCard
          icon={ShoppingCart}
          label="Orders"
          value={stats.total_orders}
          sub={stats.unseen_orders > 0 ? `${stats.unseen_orders} unseen · ${stats.paid_orders} paid` : `${stats.paid_orders} paid`}
          accent={stats.unseen_orders > 0}
        />
        <StatCard
          icon={Inbox}
          label="Messages"
          value={stats.total_orders ? (stats.unread_messages || 0) : 0}
          sub={stats.unread_messages > 0 ? `${stats.unread_messages} unread` : "all caught up"}
          accent={stats.unread_messages > 0}
        />
        <StatCard icon={Package} label="Products" value={stats.total_products} />
        <StatCard icon={BookOpen} label="Recipes" value={stats.total_recipes ?? 0} />
        <StatCard icon={FileText} label="Blog Posts" value={stats.total_blog_posts ?? 0} />
        <StatCard icon={Mail} label="Subscribers" value={stats.total_subscribers ?? 0} />
        <StatCard icon={Users} label="Customers" value={stats.total_customers} />
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div
      className={`bg-[var(--bg-surface)] border rounded-2xl p-6 ${
        accent ? "border-[var(--terracotta)]/50" : "border-[var(--border)]"
      }`}
    >
      <Icon className={`w-5 h-5 mb-3 ${accent ? "text-[var(--terracotta)]" : "text-[var(--terracotta)]"}`} />
      <p className="text-xs text-[var(--muted-fg)] uppercase tracking-wider">{label}</p>
      <p className="font-serif text-3xl text-[var(--ink-forest)] mt-1">{value}</p>
      {sub && (
        <p className={`text-xs mt-1 ${accent ? "text-[var(--terracotta)] font-medium" : "text-[var(--muted-fg)]"}`}>
          {sub}
        </p>
      )}
    </div>
  );
}

function Products() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [stockFilter, setStockFilter] = useState("all");

  const load = () => {
    api.get("/products").then((r) => setItems(r.data));
  };
  useEffect(load, []);

  const stockCounts = {
    all: items.length,
    low: items.filter((p) => stockOf(p) > 0 && stockOf(p) <= LOW_STOCK).length,
    out: items.filter((p) => stockOf(p) <= 0).length,
  };
  const shown = items.filter((p) =>
    stockFilter === "low" ? stockOf(p) > 0 && stockOf(p) <= LOW_STOCK : stockFilter === "out" ? stockOf(p) <= 0 : true
  );
  const onStockSaved = (updated) => setItems((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  return (
    <div data-testid="admin-products">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-serif text-2xl text-[var(--ink-forest)]">{items.length} products</h2>
        <button
          onClick={() => {
            setEditing(null);
            setShowForm(true);
          }}
          className="bg-[var(--terracotta)] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[var(--terracotta-deep)] transition-colors inline-flex items-center gap-2"
          data-testid="add-product-btn"
        >
          <Plus className="w-4 h-4" /> Add product
        </button>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap" data-testid="product-stock-filters">
        {[["all", "All"], ["low", "Low stock (5 or fewer)"], ["out", "Out of stock"]].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setStockFilter(id)}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
              stockFilter === id ? "bg-[var(--ink-forest)] text-[var(--bg-cream)]" : "bg-[var(--bg-surface)] border border-[var(--border)] text-[var(--ink-forest)] hover:border-[var(--ink-forest)]/30"
            }`}
            data-testid={`product-filter-${id}`}
          >
            {label}
            <span className={stockFilter === id ? "text-[var(--gold)]" : "text-[var(--muted-fg)]"}>{stockCounts[id]}</span>
          </button>
        ))}
      </div>

      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[var(--muted)]/50 text-left">
              <th className="px-4 py-3">Product</th>
              <th className="px-4 py-3 hidden md:table-cell">Category</th>
              <th className="px-4 py-3">Price</th>
              <th className="px-4 py-3">Stock</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-[var(--muted-fg)]" data-testid="product-filter-empty">Nothing here, all stocked up.</td></tr>
            )}
            {shown.map((p) => (
              <tr key={p.id} className="border-t border-[var(--border)]" data-testid={`admin-product-${p.id}`}>
                <td className="px-4 py-3 flex items-center gap-3">
                  <img src={resolveImg(p.image_url)} alt="" className="w-10 h-10 rounded-lg object-cover bg-[var(--muted)]" />
                  <span className="font-medium">{p.name}</span>
                </td>
                <td className="px-4 py-3 hidden md:table-cell text-[var(--muted-fg)]">{p.category}</td>
                <td className="px-4 py-3 font-medium">{formatPrice(p.price)}</td>
                <td className="px-4 py-3"><StockCell product={p} onSaved={onStockSaved} /></td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => {
                      setEditing(p);
                      setShowForm(true);
                    }}
                    className="p-2 hover:bg-[var(--muted)] rounded-lg"
                    data-testid={`edit-product-${p.id}`}
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(p)}
                    className="p-2 hover:bg-[var(--muted)] rounded-lg text-rose-700"
                    data-testid={`delete-product-${p.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <ProductForm
          editing={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function StockCell({ product, onSaved }) {
  const [value, setValue] = useState(String(stockOf(product)));
  const [saving, setSaving] = useState(false);
  const dirty = value !== String(stockOf(product));
  const stock = stockOf(product);

  const save = async () => {
    const n = parseInt(value, 10);
    if (Number.isNaN(n) || n < 0) {
      toast.error("Stock must be 0 or more");
      setValue(String(stock));
      return;
    }
    setSaving(true);
    try {
      const r = await api.put(`/admin/products/${product.id}`, { stock: n });
      onSaved(r.data);
      setValue(String(stockOf(r.data)));
      toast.success(`${product.name}: ${n} in stock`);
    } catch {
      toast.error("Could not save stock");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        min="0"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && dirty && save()}
        className={`w-16 bg-[var(--bg-cream)] border rounded-lg px-2 py-1 text-sm ${stock <= 0 ? "border-[var(--terracotta)]/50 text-[var(--terracotta)]" : stock <= LOW_STOCK ? "border-[var(--gold)]/60" : "border-[var(--border)]"}`}
        data-testid={`stock-input-${product.id}`}
      />
      {dirty && (
        <button onClick={save} disabled={saving} className="text-xs text-white bg-[var(--terracotta)] rounded-full px-2.5 py-1 disabled:opacity-60" data-testid={`stock-save-${product.id}`}>
          Save
        </button>
      )}
    </div>
  );
}

function ProductForm({ editing, onClose, onSaved }) {
  const [form, setForm] = useState(
    editing || {
      name: "",
      description: "",
      price: 0,
      category: "essential-oils",
      image_url: "",
      images: [],
      stock: 100,
      featured: false,
      tags: [],
    }
  );
  const [tagsText, setTagsText] = useState((editing?.tags || []).join(", "));
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        stock: parseInt(form.stock),
        tags: tagsText.split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (editing) {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Created");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[var(--ink-forest)]/40 backdrop-blur-sm" data-testid="product-form">
      <div className="bg-[var(--bg-surface)] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-[var(--border)] flex justify-between items-center sticky top-0 bg-[var(--bg-surface)]">
          <h3 className="font-serif text-2xl">{editing ? "Edit product" : "New product"}</h3>
          <button onClick={onClose} className="p-2 hover:bg-[var(--muted)] rounded-full" data-testid="close-form-btn">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <Field label="Name">
            <input
              required
              type="text"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              className="ck-input"
              data-testid="form-name"
            />
          </Field>
          <Field label="Description">
            <textarea
              required
              rows={3}
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              className="ck-input"
              data-testid="form-description"
            />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Category">
              <select
                value={form.category}
                onChange={(e) => update("category", e.target.value)}
                className="ck-input"
                data-testid="form-category"
              >
                <option value="essential-oils">Essential Oils</option>
                <option value="diy-supplies">DIY Supplies</option>
                <option value="garden-gifts">Home, Garden & Gifts</option>
              </select>
            </Field>
            <Field label="Image URL" full>
              <ImageUpload
                value={form.image_url}
                onChange={(url) => update("image_url", url)}
                label="Main product image (shown on cards)"
                testid="product-image-upload"
              />
            </Field>
            <Field label="Gallery images" full>
              <MultiImageUpload
                value={form.images || []}
                onChange={(imgs) => update("images", imgs)}
                label="Additional gallery images (shown on product page)"
                max={6}
                testid="product-gallery-upload"
              />
            </Field>
            <Field label="Price ($)">
              <input
                required
                type="number"
                step="0.01"
                min="0"
                value={form.price}
                onChange={(e) => update("price", e.target.value)}
                className="ck-input"
                data-testid="form-price"
              />
            </Field>
            <Field label="Stock">
              <input
                required
                type="number"
                min="0"
                value={form.stock}
                onChange={(e) => update("stock", e.target.value)}
                className="ck-input"
                data-testid="form-stock"
              />
            </Field>
            <Field label="Tags (comma separated)" full>
              <input
                type="text"
                value={tagsText}
                onChange={(e) => setTagsText(e.target.value)}
                className="ck-input"
                placeholder="floral, calming, single"
                data-testid="form-tags"
              />
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.featured}
              onChange={(e) => update("featured", e.target.checked)}
              data-testid="form-featured"
            />
            Featured on homepage
          </label>
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-[var(--terracotta)] text-white rounded-full py-3 text-sm font-semibold hover:bg-[var(--terracotta-deep)] disabled:opacity-60"
              data-testid="form-save-btn"
            >
              {saving ? "Saving…" : "Save product"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-5 border border-[var(--border)] rounded-full text-sm hover:bg-[var(--muted)]"
            >
              Cancel
            </button>
          </div>
        </form>
        <style>{`
          .ck-input {
            width: 100%;
            background: var(--bg-cream);
            border: 1px solid var(--border);
            border-radius: 12px;
            padding: 0.7rem 1rem;
            font-size: 0.9rem;
            color: var(--ink-forest);
            outline: none;
          }
          .ck-input:focus { border-color: var(--terracotta); }
        `}</style>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-xs font-medium uppercase tracking-wide text-[var(--ink-forest)] block mb-1.5">{label}</span>
      {children}
    </label>
  );
}

// ======================================================================
// Orders — rescue banner, filters, chips, detail panel, apology drafts
// ======================================================================
const isUnseenPaid = (o) => o.payment_status === "paid" && !o.admin_seen;

function Orders({ onBadgesChanged }) {
  const [orders, setOrders] = useState([]);
  const [recovered, setRecovered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/orders"),
      api.get("/admin/orders/recovered"),
    ])
      .then(([a, b]) => {
        setOrders(a.data || []);
        setRecovered(b.data || []);
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  // Deep-link: ?open=<orderId>
  useEffect(() => {
    const target = searchParams.get("open");
    if (target && orders.find((o) => o.id === target)) {
      setOpenId(target);
    }
  }, [orders, searchParams]);

  const unseenRecovered = recovered.filter((o) => !o.admin_seen);

  const filtered = useMemo(() => {
    let list = orders;
    if (filter === "unseen") list = list.filter(isUnseenPaid);
    else if (filter === "week") list = list.filter((o) => inThisWeek(o.created_at));
    else if (filter === "month") list = list.filter((o) => inThisMonth(o.created_at));
    else if (filter === "shipped") list = list.filter((o) => ["shipped", "delivered"].includes(orderStep(o)));
    else if (filter === "to_order") list = list.filter((o) => orderStep(o) === "new");
    else if (filter === "supplier") list = list.filter(isWaitingOnSupplier);
    else if (filter === "to_ship") list = list.filter((o) => NOT_SHIPPED_STEPS.includes(orderStep(o)));
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, filter]);

  const openOrder = orders.find((o) => o.id === openId) || null;

  const markSeen = async (id, seen = true) => {
    try {
      await api.put(`/admin/orders/${id}/seen`, { seen });
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, admin_seen: seen } : o)));
      setRecovered((prev) => prev.map((o) => (o.id === id ? { ...o, admin_seen: seen } : o)));
      onBadgesChanged && onBadgesChanged();
    } catch { toast.error("Could not update"); }
  };

  const replaceOrder = (updated) => setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));

  const handleOpen = (id) => {
    setOpenId(id);
    // Auto-mark seen on open.
    const o = orders.find((x) => x.id === id);
    if (o && !o.admin_seen) markSeen(id, true);
    const next = new URLSearchParams(searchParams);
    next.set("open", id);
    setSearchParams(next, { replace: true });
  };

  const handleClose = () => {
    setOpenId(null);
    const next = new URLSearchParams(searchParams);
    next.delete("open");
    setSearchParams(next, { replace: true });
  };

  if (loading) return <div className="h-40 bg-[var(--muted)] rounded-2xl animate-pulse" />;

  const FILTERS = [
    { id: "all", label: "All", count: orders.length },
    { id: "unseen", label: "Unseen", count: orders.filter(isUnseenPaid).length },
    { id: "to_order", label: "To order", count: orders.filter((o) => orderStep(o) === "new").length },
    { id: "supplier", label: "Waiting on supplier", count: orders.filter(isWaitingOnSupplier).length },
    { id: "to_ship", label: "To ship", count: orders.filter((o) => NOT_SHIPPED_STEPS.includes(orderStep(o))).length },
    { id: "shipped", label: "Shipped", count: orders.filter((o) => ["shipped", "delivered"].includes(orderStep(o))).length },
    { id: "week", label: "This week" },
    { id: "month", label: "This month" },
  ];

  return (
    <div data-testid="admin-orders" className="space-y-5">
      {/* Rescue banner — shows actual recovered orders */}
      {unseenRecovered.length > 0 && (
        <div
          className="bg-[var(--bg-surface)] border-2 border-[var(--terracotta)]/35 rounded-2xl p-5 sm:p-6"
          data-testid="orders-recovered-banner"
        >
          <div className="flex items-start gap-3 mb-4">
            <Feather className="w-5 h-5 text-[var(--terracotta)] mt-0.5 shrink-0" strokeWidth={1.5} />
            <div>
              <p className="eyebrow text-[var(--terracotta)] mb-1">Recovered orders</p>
              <h3 className="font-serif text-xl text-[var(--ink-forest)] leading-tight">
                {unseenRecovered.length} order{unseenRecovered.length === 1 ? "" : "s"} came in and slipped past us.
              </h3>
              <p className="text-sm text-[var(--muted-fg)] mt-1.5">
                Open each one to review the items and send a warm note. Clicking the row marks it seen.
              </p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-2">
            {unseenRecovered.map((o) => (
              <button
                key={o.id}
                onClick={() => handleOpen(o.id)}
                className="bg-[var(--bg-cream)] border border-[var(--border)] rounded-xl p-4 text-left hover:border-[var(--terracotta)]/40 transition-colors"
                data-testid={`recovered-card-${o.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-serif text-lg text-[var(--ink-forest)]">{o.customer_name}{o.practice_copy && <PracticeCopyTag testid={`recovered-practice-tag-${o.id}`} />}</p>
                    <p className="text-xs text-[var(--muted-fg)]">{o.customer_email}</p>
                  </div>
                  <StepChip order={o} />
                </div>
                <p className="text-xs text-[var(--muted-fg)] mt-2">
                  {mtDate(o.created_at)} · {o.items.length} item{o.items.length === 1 ? "" : "s"} · {formatPrice(o.total)}
                </p>
                {o.apology_sent ? (
                  <p className="text-[11px] mt-2 text-[var(--terracotta)] inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Apology sent
                  </p>
                ) : (
                  <p className="text-[11px] mt-2 text-[var(--muted-fg)]">Open to review the apology draft</p>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="orders-filters">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`shrink-0 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
              filter === f.id
                ? "bg-[var(--ink-forest)] text-[var(--bg-cream)]"
                : "bg-[var(--bg-surface)] border border-[var(--border)] text-[var(--ink-forest)] hover:border-[var(--ink-forest)]/30"
            }`}
            data-testid={`orders-filter-${f.id}`}
          >
            {f.label}
            {typeof f.count === "number" && (
              <span className={filter === f.id ? "text-[var(--gold)]" : "text-[var(--muted-fg)]"}>
                {f.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <p className="text-[var(--muted-fg)] text-sm py-6">No orders in this view.</p>
      ) : (
        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-2xl overflow-hidden">
          {filtered.map((o, idx) => (
            <button
              key={o.id}
              onClick={() => handleOpen(o.id)}
              className={`w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-[var(--muted)]/50 transition-colors ${
                idx === 0 ? "" : "border-t border-[var(--border)]"
              } ${!o.admin_seen ? "bg-[var(--terracotta)]/[0.04]" : ""} ${isOverdue(o) ? "border-l-4 border-l-[var(--gold)]" : ""}`}
              data-testid={`admin-order-row-${o.id}`}
            >
              <span
                className={`w-2 h-2 rounded-full shrink-0 ${
                  !o.admin_seen ? "bg-[var(--terracotta)]" : "bg-transparent"
                }`}
                aria-hidden
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-serif text-base text-[var(--ink-forest)] truncate">{o.customer_name}</span>
                  {o.recovered && (
                    <span className="text-[10px] uppercase tracking-wider text-[var(--terracotta)] bg-[var(--terracotta)]/10 px-1.5 py-0.5 rounded">
                      recovered
                    </span>
                  )}
                  {o.practice_copy && <PracticeCopyTag testid={`practice-copy-tag-${o.id}`} />}
                </div>
                <p className="text-xs text-[var(--muted-fg)] truncate">
                  <span className="font-mono">{o.order_number}</span> · {o.items.length} item{o.items.length === 1 ? "" : "s"} · {o.customer_email}
                </p>
                {isOverdue(o) && (
                  <p className="text-[11px] text-[#8b6a2a] mt-0.5" data-testid={`order-overdue-${o.id}`}>
                    Paid {daysSincePaid(o)} days ago · not shipped yet
                  </p>
                )}
              </div>
              <div className="text-right shrink-0 hidden sm:block">
                <p className="text-sm font-medium text-[var(--ink-forest)]">{formatPrice(o.total)}</p>
                <p className="text-[11px] text-[var(--muted-fg)]">
                  {mtDate(o.created_at, { month: "short", day: "numeric" })}
                </p>
              </div>
              <StepChip order={o} />
            </button>
          ))}
        </div>
      )}

      {/* Detail drawer */}
      {openOrder && (
        <OrderDetailDrawer
          order={openOrder}
          onClose={handleClose}
          onMarkSeen={(seen) => markSeen(openOrder.id, seen)}
          onUpdated={replaceOrder}
          onAfterApology={() => load()}
        />
      )}
    </div>
  );
}

function PracticeCopyTag({ testid }) {
  return (
    <span
      className="ml-1.5 align-middle text-[10px] uppercase tracking-wider font-sans text-[var(--ink-forest)] bg-[var(--ink-forest)]/5 border border-dashed border-[var(--ink-forest)]/30 px-1.5 py-0.5 rounded"
      title="A preview practice copy of a real order. It never emails the customer."
      data-testid={testid}
    >
      Practice copy
    </span>
  );
}

function OrderDetailDrawer({ order, onClose, onMarkSeen, onUpdated, onAfterApology }) {
  const [showApology, setShowApology] = useState(false);
  const addr = order.shipping_address || {};
  return (
    <div
      className="fixed inset-0 z-50 bg-[var(--ink-forest)]/30 backdrop-blur-sm flex items-stretch justify-end"
      onClick={onClose}
      data-testid="order-detail-overlay"
    >
      <div
        className="bg-[var(--bg-cream)] w-full sm:max-w-xl shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="order-detail-panel"
      >
        <div className="sticky top-0 bg-[var(--bg-cream)]/95 backdrop-blur border-b border-[var(--border)] px-6 py-4 flex items-center justify-between">
          <div>
            <p className="eyebrow text-[var(--terracotta)]">Order</p>
            <p className="font-mono text-sm text-[var(--ink-forest)]">{order.order_number}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-[var(--muted)]"
            aria-label="Close"
            data-testid="order-detail-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-6">
          <div className="flex flex-wrap items-center gap-2">
            <StepChip order={order} size="md" />
            {order.payment_status === "paid" && order.status !== "paid" && (
              <span className="text-[11px] bg-[var(--ink-forest)]/8 border border-[var(--ink-forest)]/20 text-[var(--ink-forest)] px-2 py-0.5 rounded-full" data-testid="order-payment-chip">
                Payment: paid
              </span>
            )}
            {order.recovered && (
              <span className="text-[11px] bg-[var(--terracotta)]/10 border border-[var(--terracotta)]/30 text-[var(--terracotta)] px-2 py-0.5 rounded-full">
                recovered
              </span>
            )}
            {order.practice_copy && <PracticeCopyTag testid="order-practice-copy-tag" />}
            {order.apology_sent && (
              <span className="text-[11px] bg-[var(--terracotta)]/15 border border-[var(--terracotta)]/40 text-[var(--terracotta-deep)] px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> apology sent
              </span>
            )}
          </div>

          <div>
            <h2 className="font-serif text-2xl text-[var(--ink-forest)] leading-tight">{order.customer_name}</h2>
            <p className="text-sm text-[var(--muted-fg)]">
              <a href={`mailto:${order.customer_email}`} className="text-[var(--terracotta)]">{order.customer_email}</a>
              {addr.phone && <> · {addr.phone}</>}
            </p>
            <p className="text-xs text-[var(--muted-fg)] mt-1">
              {mtDateTime(order.created_at)}
            </p>
          </div>

          <OrderItemsPanel order={order} onUpdated={onUpdated} />

          <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-4">
            <p className="eyebrow mb-2">Ship to</p>
            <p className="text-sm text-[var(--ink-forest)] leading-relaxed">
              {addr.name}<br />
              {addr.address_line1}{addr.address_line2 ? <>, {addr.address_line2}</> : null}<br />
              {addr.city}, {addr.state} {addr.zip}
            </p>
            {order.stripe_session_id && (
              <p className="text-[11px] text-[var(--muted-fg)] mt-3 font-mono truncate">
                Stripe: {order.stripe_session_id}
              </p>
            )}
          </div>

          <OrderFulfillment order={order} onUpdated={onUpdated} />

          <div className="flex flex-wrap gap-2 border-t border-[var(--border)] pt-5">
            <button
              onClick={() => onMarkSeen(!order.admin_seen)}
              className="bg-[var(--bg-surface)] border border-[var(--border)] text-[var(--ink-forest)] rounded-full px-4 py-2 text-sm font-medium hover:border-[var(--ink-forest)]/30 transition-colors inline-flex items-center gap-2"
              data-testid="order-mark-seen-btn"
            >
              <Eye className="w-4 h-4" /> {order.admin_seen ? "Mark unseen" : "Mark as seen"}
            </button>
            <button
              onClick={() => setShowApology(true)}
              className="bg-[var(--terracotta)] text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-[var(--terracotta-deep)] transition-colors inline-flex items-center gap-2"
              data-testid="order-apology-btn"
            >
              <PenLine className="w-4 h-4" /> {order.apology_sent ? "Send another note" : "Write apology"}
            </button>
          </div>
        </div>

        {showApology && (
          <ApologyDraftModal
            order={order}
            onClose={() => setShowApology(false)}
            onSent={() => { setShowApology(false); onAfterApology && onAfterApology(); }}
          />
        )}
      </div>
    </div>
  );
}

function ApologyDraftModal({ order, onClose, onSent }) {
  const [draft, setDraft] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [path, setPath] = useState("choose"); // choose | edit | copy

  useEffect(() => {
    api.post(`/admin/orders/${order.id}/apology-draft`).then((r) => {
      setDraft(r.data);
      setSubject(r.data.subject);
      setBody(r.data.body_text);
    }).catch(() => toast.error("Could not load draft"));
  }, [order.id]);

  const copyAll = async () => {
    const text = `Subject: ${subject}\n\n${body}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied — paste it into your email");
    } catch {
      toast.error("Could not copy to clipboard");
    }
  };

  const send = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject and message cannot be empty");
      return;
    }
    const prompt = draft.already_sent
      ? `You already sent a note to ${order.customer_email}${draft.sent_at ? ` on ${mtDate(draft.sent_at, { month: "short", day: "numeric" })}` : ""}. Send another one?`
      : `Send this note to ${order.customer_email} now?`;
    if (!window.confirm(prompt)) return;
    setSending(true);
    try {
      await api.post(`/admin/orders/${order.id}/send-apology`, {
        subject,
        body_text: body,
        resend: !!draft.already_sent,
      });
      toast.success("Apology sent to " + order.customer_email);
      onSent && onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-[var(--ink-forest)]/50 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="apology-modal-overlay"
    >
      <div
        className="bg-[var(--bg-cream)] w-full max-w-2xl rounded-2xl shadow-2xl my-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="apology-modal"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <div>
            <p className="eyebrow text-[var(--terracotta)]">Apology to {order.customer_name}</p>
            <p className="text-xs text-[var(--muted-fg)]">{order.customer_email}</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="p-2 rounded-full hover:bg-[var(--muted)]" data-testid="apology-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!draft ? (
          <div className="p-6 h-40 bg-[var(--muted)] animate-pulse" />
        ) : (
          <div className="p-6 space-y-5">
            {path === "choose" && (
              <>
                <p className="text-sm text-[var(--muted-fg)] leading-relaxed">
                  I've drafted a note from Sunny, grounded in {order.customer_name.split(" ")[0]}'s actual order. Pick how you'd like to send it:
                </p>
                <div className="grid sm:grid-cols-3 gap-3">
                  <button
                    onClick={() => setPath("copy")}
                    className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-4 text-left hover:border-[var(--ink-forest)]/30"
                    data-testid="apology-path-copy"
                  >
                    <Copy className="w-5 h-5 text-[var(--terracotta)] mb-2" />
                    <p className="font-serif text-base text-[var(--ink-forest)]">Copy &amp; send yourself</p>
                    <p className="text-xs text-[var(--muted-fg)] mt-1">Open your email, paste, send — so it comes from a real person.</p>
                  </button>
                  <button
                    onClick={() => setPath("edit")}
                    className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-4 text-left hover:border-[var(--ink-forest)]/30"
                    data-testid="apology-path-edit"
                  >
                    <PenLine className="w-5 h-5 text-[var(--terracotta)] mb-2" />
                    <p className="font-serif text-base text-[var(--ink-forest)]">Edit, then send</p>
                    <p className="text-xs text-[var(--muted-fg)] mt-1">Adjust the words and we'll send it through the shop email.</p>
                  </button>
                  <button
                    onClick={send}
                    disabled={sending}
                    className="bg-[var(--terracotta)] text-white rounded-xl p-4 text-left hover:bg-[var(--terracotta-deep)] transition-colors disabled:opacity-60"
                    data-testid="apology-path-send"
                  >
                    <Send className="w-5 h-5 mb-2" />
                    <p className="font-serif text-base">Send as-is</p>
                    <p className="text-xs text-white/80 mt-1">Looks good — send the draft (we'll ask once to confirm).</p>
                  </button>
                </div>
                <details className="mt-2">
                  <summary className="text-xs text-[var(--muted-fg)] cursor-pointer">Preview the draft</summary>
                  <div className="mt-3 border border-[var(--border)] rounded-xl p-4 bg-[var(--bg-surface)]">
                    <p className="text-xs text-[var(--muted-fg)] mb-1">Subject</p>
                    <p className="font-medium text-[var(--ink-forest)] mb-3">{subject}</p>
                    <p className="text-xs text-[var(--muted-fg)] mb-1">Body</p>
                    <pre className="whitespace-pre-wrap text-sm text-[var(--ink-forest)] font-serif leading-relaxed">{body}</pre>
                  </div>
                </details>
              </>
            )}

            {path === "copy" && (
              <>
                <div className="rounded-xl bg-[var(--bg-surface)] border border-[var(--border)] p-4">
                  <p className="text-xs text-[var(--muted-fg)] mb-1">Subject</p>
                  <p className="font-medium text-[var(--ink-forest)] mb-3">{subject}</p>
                  <p className="text-xs text-[var(--muted-fg)] mb-1">Body</p>
                  <pre className="whitespace-pre-wrap text-sm text-[var(--ink-forest)] font-serif leading-relaxed">{body}</pre>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={copyAll}
                    className="bg-[var(--terracotta)] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[var(--terracotta-deep)] transition-colors inline-flex items-center gap-2"
                    data-testid="apology-copy-btn"
                  >
                    <Copy className="w-4 h-4" /> Copy everything
                  </button>
                  <a
                    href={`mailto:${order.customer_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`}
                    className="bg-[var(--bg-surface)] border border-[var(--border)] text-[var(--ink-forest)] rounded-full px-5 py-2.5 text-sm font-medium hover:border-[var(--ink-forest)]/30 transition-colors inline-flex items-center gap-2"
                    data-testid="apology-mailto-btn"
                  >
                    Open in email app
                  </a>
                  <button onClick={() => setPath("choose")} className="text-xs text-[var(--muted-fg)] underline">
                    Back
                  </button>
                </div>
              </>
            )}

            {path === "edit" && (
              <>
                <div className="space-y-3">
                  <label className="block">
                    <span className="text-xs text-[var(--muted-fg)]">Subject</span>
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="w-full mt-1 bg-[var(--bg-surface)] border border-[var(--border)] rounded-lg px-3 py-2 text-sm"
                      data-testid="apology-subject-input"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-[var(--muted-fg)]">Message</span>
                    <textarea
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      rows={16}
                      className="w-full mt-1 bg-[var(--bg-surface)] border border-[var(--border)] rounded-lg px-3 py-2 text-sm font-serif leading-relaxed"
                      data-testid="apology-body-input"
                    />
                  </label>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={send}
                    disabled={sending}
                    className="bg-[var(--terracotta)] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[var(--terracotta-deep)] transition-colors inline-flex items-center gap-2 disabled:opacity-60"
                    data-testid="apology-send-edited-btn"
                  >
                    <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send edited note"}
                  </button>
                  <button onClick={() => setPath("choose")} className="text-xs text-[var(--muted-fg)] underline">
                    Back
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Messages({ onBadgesChanged }) {
  const [messages, setMessages] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = () => {
    setLoading(true);
    api.get("/admin/messages").then((r) => setMessages(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  useEffect(() => {
    const target = searchParams.get("open");
    if (target && messages.find((m) => m.id === target)) setOpenId(target);
  }, [messages, searchParams]);

  const toggleSeen = async (id, seen) => {
    try {
      await api.put(`/admin/messages/${id}/seen`, { seen });
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, admin_seen: seen } : m)));
      onBadgesChanged && onBadgesChanged();
    } catch { toast.error("Could not update"); }
  };

  const removeMsg = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.delete(`/admin/messages/${id}`);
      setMessages((prev) => prev.filter((m) => m.id !== id));
      setOpenId(null);
      onBadgesChanged && onBadgesChanged();
    } catch { toast.error("Could not delete"); }
  };

  const open = messages.find((m) => m.id === openId) || null;

  const handleOpen = (id) => {
    setOpenId(id);
    const m = messages.find((x) => x.id === id);
    if (m && !m.admin_seen) toggleSeen(id, true);
    const next = new URLSearchParams(searchParams);
    next.set("open", id);
    setSearchParams(next, { replace: true });
  };

  const handleClose = () => {
    setOpenId(null);
    const next = new URLSearchParams(searchParams);
    next.delete("open");
    setSearchParams(next, { replace: true });
  };

  if (loading) return <div className="h-40 bg-[var(--muted)] rounded-2xl animate-pulse" />;

  return (
    <div data-testid="admin-messages" className="space-y-5">
      {messages.length === 0 ? (
        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-2xl p-10 text-center">
          <Inbox className="w-7 h-7 text-[var(--terracotta)] mx-auto mb-3" />
          <p className="font-serif text-xl text-[var(--ink-forest)]">No messages yet</p>
          <p className="text-sm text-[var(--muted-fg)] mt-1">
            Customers can write to you from the <a className="text-[var(--terracotta)] underline" href="/contact" target="_blank" rel="noreferrer">contact page</a>. Everything lands here and in your email.
          </p>
        </div>
      ) : (
        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-2xl overflow-hidden">
          {messages.map((m, idx) => (
            <button
              key={m.id}
              onClick={() => handleOpen(m.id)}
              className={`w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-[var(--muted)]/50 transition-colors ${idx === 0 ? "" : "border-t border-[var(--border)]"} ${!m.admin_seen ? "bg-[var(--terracotta)]/[0.04]" : ""}`}
              data-testid={`admin-message-row-${m.id}`}
            >
              <span className={`w-2 h-2 rounded-full shrink-0 ${!m.admin_seen ? "bg-[var(--terracotta)]" : "bg-transparent"}`} aria-hidden />
              <div className="flex-1 min-w-0">
                <p className="font-serif text-base text-[var(--ink-forest)] truncate">{m.name}</p>
                <p className="text-xs text-[var(--muted-fg)] truncate">{m.email} · {m.message.slice(0, 80)}</p>
              </div>
              <span className="text-[11px] text-[var(--muted-fg)] shrink-0 hidden sm:inline">
                {mtDate(m.created_at, { month: "short", day: "numeric" })}
              </span>
            </button>
          ))}
        </div>
      )}

      {open && (
        <div
          className="fixed inset-0 z-50 bg-[var(--ink-forest)]/30 backdrop-blur-sm flex items-stretch justify-end"
          onClick={handleClose}
          data-testid="message-detail-overlay"
        >
          <div
            className="bg-[var(--bg-cream)] w-full sm:max-w-xl shadow-2xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="message-detail-panel"
          >
            <div className="sticky top-0 bg-[var(--bg-cream)]/95 backdrop-blur border-b border-[var(--border)] px-6 py-4 flex items-center justify-between">
              <div>
                <p className="eyebrow text-[var(--terracotta)]">Message</p>
                <p className="font-serif text-lg text-[var(--ink-forest)]">{open.name}</p>
              </div>
              <button onClick={handleClose} aria-label="Close" className="p-2 rounded-full hover:bg-[var(--muted)]" data-testid="message-detail-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-[var(--muted-fg)]">
                <a href={`mailto:${open.email}?subject=${encodeURIComponent("Re: your note to DC Gift World Too")}`} className="text-[var(--terracotta)]">{open.email}</a>
                {" · "}
                {mtDateTime(open.created_at)}
              </p>
              {open.order_number && (
                <p className="text-xs text-[var(--muted-fg)]">
                  About order <span className="font-mono text-[var(--ink-forest)]">{open.order_number}</span>
                </p>
              )}
              <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-4 font-serif text-base leading-relaxed text-[var(--ink-forest)] whitespace-pre-line">
                {open.message}
              </div>
              <div className="flex flex-wrap gap-2">
                <a
                  href={`mailto:${open.email}?subject=${encodeURIComponent("Re: your note to DC Gift World Too")}&body=${encodeURIComponent(`Hi ${open.name.split(" ")[0]},\n\n`)}`}
                  className="bg-[var(--terracotta)] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[var(--terracotta-deep)] transition-colors inline-flex items-center gap-2"
                  data-testid="message-reply-btn"
                >
                  <Send className="w-4 h-4" /> Reply in your email
                </a>
                <button
                  onClick={() => toggleSeen(open.id, !open.admin_seen)}
                  className="bg-[var(--bg-surface)] border border-[var(--border)] text-[var(--ink-forest)] rounded-full px-5 py-2.5 text-sm font-medium hover:border-[var(--ink-forest)]/30 transition-colors inline-flex items-center gap-2"
                  data-testid="message-mark-seen-btn"
                >
                  <Eye className="w-4 h-4" /> {open.admin_seen ? "Mark unread" : "Mark as read"}
                </button>
                <button
                  onClick={() => removeMsg(open.id)}
                  className="text-sm text-[var(--muted-fg)] hover:text-[var(--terracotta)] inline-flex items-center gap-2 ml-auto"
                  data-testid="message-delete-btn"
                >
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SettingsPanel() {
  const [recipients, setRecipients] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/alert-settings").then((r) => setRecipients(r.data.recipients || [])).catch(() => {});
  }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      const r = await api.put("/admin/alert-settings", { recipients: next });
      setRecipients(r.data.recipients);
      toast.success("Alert recipients updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const toggle = (idx) => {
    const next = recipients.map((r, i) => (i === idx ? { ...r, active: !r.active } : r));
    save(next);
  };

  const remove = (idx) => {
    const next = recipients.filter((_, i) => i !== idx);
    if (!next.some((r) => r.active)) {
      toast.error("Keep at least one active inbox so no order is missed");
      return;
    }
    if (!window.confirm(`Stop sending alerts to ${recipients[idx].email}?`)) return;
    save(next);
  };

  const add = () => {
    const e = newEmail.trim().toLowerCase();
    if (!e || !e.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    if (recipients.find((r) => r.email === e)) {
      toast.error("Already in the list");
      return;
    }
    save([...recipients, { email: e, active: true }]);
    setNewEmail("");
  };

  const activeCount = recipients.filter((r) => r.active).length;

  return (
    <div className="max-w-2xl space-y-6" data-testid="admin-settings">
      <div>
        <h2 className="font-serif text-2xl text-[var(--ink-forest)]">Order &amp; message alerts</h2>
        <p className="text-sm text-[var(--muted-fg)] mt-1">
          Every new paid order and every customer message is emailed to the inboxes below. Turn one off if you'd like — alerts still go to the others.
        </p>
      </div>

      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-2xl overflow-hidden">
        {recipients.length === 0 ? (
          <p className="px-5 py-6 text-sm text-[var(--muted-fg)]">No recipients. Add one below so you don't miss a thing.</p>
        ) : (
          recipients.map((r, idx) => (
            <div
              key={r.email + idx}
              className={`px-5 py-4 flex items-center gap-3 ${idx === 0 ? "" : "border-t border-[var(--border)]"}`}
              data-testid={`alert-recipient-${idx}`}
            >
              <div className="flex-1 min-w-0">
                <p className={`text-sm ${r.active ? "text-[var(--ink-forest)]" : "text-[var(--muted-fg)] line-through"}`}>{r.email}</p>
                <p className="text-[11px] text-[var(--muted-fg)]">{r.active ? "Receiving alerts" : "Paused"}</p>
              </div>
              <button
                onClick={() => toggle(idx)}
                disabled={saving || (r.active && activeCount <= 1)}
                className={`text-xs rounded-full px-3 py-1.5 border font-medium ${
                  r.active
                    ? "border-[var(--ink-forest)]/20 text-[var(--ink-forest)] hover:bg-[var(--muted)]"
                    : "border-[var(--terracotta)]/40 text-[var(--terracotta)] hover:bg-[var(--terracotta)]/8"
                } disabled:opacity-50`}
                title={r.active && activeCount <= 1 ? "Keep at least one active recipient" : undefined}
                data-testid={`alert-toggle-${idx}`}
              >
                {r.active ? "Pause" : "Resume"}
              </button>
              <button
                onClick={() => remove(idx)}
                disabled={saving || (r.active && activeCount <= 1)}
                title={r.active && activeCount <= 1 ? "Keep at least one active recipient" : undefined}
                className="text-[var(--muted-fg)] hover:text-[var(--terracotta)]"
                aria-label="Remove"
                data-testid={`alert-remove-${idx}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))
        )}
      </div>

      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-2xl p-5">
        <p className="eyebrow mb-2">Add an inbox</p>
        <div className="flex gap-2 flex-wrap">
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="you@example.com"
            className="flex-1 bg-[var(--bg-cream)] border border-[var(--border)] rounded-lg px-3 py-2 text-sm min-w-[220px]"
            data-testid="alert-new-email"
          />
          <button
            onClick={add}
            disabled={saving}
            className="bg-[var(--terracotta)] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[var(--terracotta-deep)] transition-colors inline-flex items-center gap-2 disabled:opacity-60"
            data-testid="alert-add-btn"
          >
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
      </div>

      <div className="bg-[var(--bg-cream)] border border-[var(--terracotta)]/30 rounded-xl p-4 flex items-start gap-3">
        <AlertCircle className="w-4 h-4 text-[var(--terracotta)] mt-0.5 shrink-0" />
        <p className="text-xs text-[var(--muted-fg)] leading-relaxed">
          Alert emails are sent from your shop's address and arrive seconds after a customer checks out. Both inboxes get a copy by default so a missed check on one is caught by the other.
        </p>
      </div>
    </div>
  );
}

// ======================================================================
// (end orders / messages / settings — original admin follows)
// ======================================================================
function OrdersLegacyPlaceholder() { return null; }


function Recipes() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/recipes").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete "${r.title}"?`)) return;
    try {
      await api.delete(`/admin/recipes/${r.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div data-testid="admin-recipes">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-serif text-2xl text-[var(--ink-forest)]">{items.length} recipes</h2>
        <button
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="bg-[var(--terracotta)] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[var(--terracotta-deep)] transition-colors inline-flex items-center gap-2"
          data-testid="add-recipe-btn"
        >
          <Plus className="w-4 h-4" /> Add recipe
        </button>
      </div>

      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[var(--muted)]/50 text-left">
              <th className="px-4 py-3">Recipe</th>
              <th className="px-4 py-3 hidden md:table-cell">Category</th>
              <th className="px-4 py-3 hidden md:table-cell">Time</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-t border-[var(--border)]">
                <td className="px-4 py-3 flex items-center gap-3">
                  <img src={resolveImg(r.image_url)} alt="" className="w-10 h-10 rounded-lg object-cover bg-[var(--muted)]" />
                  <span className="font-medium">{r.title}</span>
                </td>
                <td className="px-4 py-3 hidden md:table-cell text-[var(--muted-fg)]">{r.category}</td>
                <td className="px-4 py-3 hidden md:table-cell">{r.time_minutes} min</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => { setEditing(r); setShowForm(true); }} className="p-2 hover:bg-[var(--muted)] rounded-lg" data-testid={`edit-recipe-${r.id}`}>
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(r)} className="p-2 hover:bg-[var(--muted)] rounded-lg text-rose-700" data-testid={`delete-recipe-${r.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <RecipeForm editing={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />
      )}
    </div>
  );
}

function RecipeForm({ editing, onClose, onSaved }) {
  const [form, setForm] = useState(
    editing || {
      title: "",
      slug: "",
      category: "diffuser-blends",
      short_description: "",
      image_url: "",
      difficulty: "easy",
      time_minutes: 10,
      yield_text: "",
      ingredients: [{ name: "", amount: "", product_id: "" }],
      steps: [""],
      tips: "",
      featured: false,
    }
  );
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateIng = (i, k, v) =>
    setForm((f) => ({ ...f, ingredients: f.ingredients.map((ing, idx) => (idx === i ? { ...ing, [k]: v } : ing)) }));
  const updateStep = (i, v) =>
    setForm((f) => ({ ...f, steps: f.steps.map((s, idx) => (idx === i ? v : s)) }));
  const addIng = () => setForm((f) => ({ ...f, ingredients: [...f.ingredients, { name: "", amount: "", product_id: "" }] }));
  const removeIng = (i) => setForm((f) => ({ ...f, ingredients: f.ingredients.filter((_, idx) => idx !== i) }));
  const addStep = () => setForm((f) => ({ ...f, steps: [...f.steps, ""] }));
  const removeStep = (i) => setForm((f) => ({ ...f, steps: f.steps.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        time_minutes: parseInt(form.time_minutes),
        ingredients: form.ingredients
          .filter((i) => i.name.trim())
          .map((i) => ({ ...i, product_id: i.product_id || null })),
        steps: form.steps.filter((s) => s.trim()),
      };
      if (editing) {
        await api.put(`/admin/recipes/${editing.id}`, payload);
        toast.success("Recipe updated");
      } else {
        await api.post("/admin/recipes", payload);
        toast.success("Recipe created");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[var(--ink-forest)]/40 backdrop-blur-sm" data-testid="recipe-form">
      <div className="bg-[var(--bg-surface)] rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-[var(--border)] flex justify-between items-center sticky top-0 bg-[var(--bg-surface)] z-10">
          <h3 className="font-serif text-2xl">{editing ? "Edit recipe" : "New recipe"}</h3>
          <button onClick={onClose} className="p-2 hover:bg-[var(--muted)] rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Title">
              <input required value={form.title} onChange={(e) => update("title", e.target.value)} className="ck-input" />
            </Field>
            <Field label="Slug (url)">
              <input
                required
                value={form.slug}
                onChange={(e) => update("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
                placeholder="lavender-linen-spray"
                className="ck-input"
              />
            </Field>
          </div>
          <Field label="Short description">
            <textarea required rows={2} value={form.short_description} onChange={(e) => update("short_description", e.target.value)} className="ck-input" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Category">
              <select value={form.category} onChange={(e) => update("category", e.target.value)} className="ck-input">
                <option value="diffuser-blends">Diffuser Blends</option>
                <option value="roller-bottles">Roller Bottles</option>
                <option value="sprays">Sprays & Mists</option>
                <option value="candles">Candles</option>
                <option value="soaps">Soaps & Bath</option>
                <option value="cleaning">Natural Cleaning</option>
              </select>
            </Field>
            <Field label="Image" full>
              <ImageUpload
                value={form.image_url}
                onChange={(url) => update("image_url", url)}
                label="Recipe image"
                testid="recipe-image-upload"
              />
            </Field>
            <Field label="Difficulty">
              <select value={form.difficulty} onChange={(e) => update("difficulty", e.target.value)} className="ck-input">
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="advanced">Advanced</option>
              </select>
            </Field>
            <Field label="Time (minutes)">
              <input type="number" min="1" value={form.time_minutes} onChange={(e) => update("time_minutes", e.target.value)} className="ck-input" />
            </Field>
            <Field label="Yield" full>
              <input value={form.yield_text} onChange={(e) => update("yield_text", e.target.value)} placeholder="1 × 4oz spray bottle" className="ck-input" />
            </Field>
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <p className="text-xs font-medium uppercase tracking-wide text-[var(--ink-forest)]">Ingredients</p>
              <button type="button" onClick={addIng} className="text-xs text-[var(--terracotta)] font-medium">+ Add</button>
            </div>
            <div className="space-y-2">
              {form.ingredients.map((ing, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <input placeholder="Name" value={ing.name} onChange={(e) => updateIng(i, "name", e.target.value)} className="ck-input col-span-4" />
                  <input placeholder="Amount" value={ing.amount} onChange={(e) => updateIng(i, "amount", e.target.value)} className="ck-input col-span-3" />
                  <select value={ing.product_id || ""} onChange={(e) => updateIng(i, "product_id", e.target.value)} className="ck-input col-span-4">
                    <option value="">— Link product (optional) —</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  <button type="button" onClick={() => removeIng(i)} className="col-span-1 text-rose-700 p-2">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <p className="text-xs font-medium uppercase tracking-wide text-[var(--ink-forest)]">Steps</p>
              <button type="button" onClick={addStep} className="text-xs text-[var(--terracotta)] font-medium">+ Add</button>
            </div>
            <div className="space-y-2">
              {form.steps.map((step, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <span className="pt-2 w-6 text-xs text-[var(--muted-fg)]">{i + 1}.</span>
                  <textarea rows={2} placeholder="Step instruction…" value={step} onChange={(e) => updateStep(i, e.target.value)} className="ck-input flex-1" />
                  <button type="button" onClick={() => removeStep(i)} className="text-rose-700 p-2">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <Field label="Tips (optional)">
            <textarea rows={2} value={form.tips} onChange={(e) => update("tips", e.target.value)} className="ck-input" />
          </Field>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.featured} onChange={(e) => update("featured", e.target.checked)} />
            Featured on homepage
          </label>

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} className="flex-1 bg-[var(--terracotta)] text-white rounded-full py-3 text-sm font-semibold hover:bg-[var(--terracotta-deep)] disabled:opacity-60">
              {saving ? "Saving…" : "Save recipe"}
            </button>
            <button type="button" onClick={onClose} className="px-5 border border-[var(--border)] rounded-full text-sm hover:bg-[var(--muted)]">
              Cancel
            </button>
          </div>
        </form>

        <style>{`
          .ck-input {
            width: 100%;
            background: var(--bg-cream);
            border: 1px solid var(--border);
            border-radius: 12px;
            padding: 0.7rem 1rem;
            font-size: 0.9rem;
            color: var(--ink-forest);
            outline: none;
          }
          .ck-input:focus { border-color: var(--terracotta); }
        `}</style>
      </div>
    </div>
  );
}

function Blog() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/admin/blog").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try {
      await api.delete(`/admin/blog/${p.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div data-testid="admin-blog">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-serif text-2xl text-[var(--ink-forest)]">{items.length} blog posts</h2>
        <button
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="bg-[var(--terracotta)] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[var(--terracotta-deep)] inline-flex items-center gap-2"
          data-testid="add-blog-btn"
        >
          <Plus className="w-4 h-4" /> New post
        </button>
      </div>

      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[var(--muted)]/50 text-left">
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3 hidden md:table-cell">Category</th>
              <th className="px-4 py-3 hidden md:table-cell">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-[var(--border)]" data-testid={`admin-blog-post-${p.id}`}>
                <td className="px-4 py-3 flex items-center gap-3">
                  <img src={p.cover_image} alt="" className="w-10 h-10 rounded-lg object-cover bg-[var(--muted)]" />
                  <span className="font-medium">{p.title}</span>
                </td>
                <td className="px-4 py-3 hidden md:table-cell text-[var(--muted-fg)]">{p.category}</td>
                <td className="px-4 py-3 hidden md:table-cell">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${p.published ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-900"}`}>
                    {p.published ? "Published" : "Draft"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => { setEditing(p); setShowForm(true); }} className="p-2 hover:bg-[var(--muted)] rounded-lg" data-testid={`edit-blog-${p.id}`}>
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(p)} className="p-2 hover:bg-[var(--muted)] rounded-lg text-rose-700" data-testid={`delete-blog-${p.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <BlogForm editing={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />
      )}
    </div>
  );
}

function BlogForm({ editing, onClose, onSaved }) {
  const [form, setForm] = useState(
    editing || {
      title: "",
      slug: "",
      excerpt: "",
      content: "",
      cover_image: "",
      author: "DC Gift World Too",
      category: "general",
      tags: [],
      featured: false,
      published: true,
    }
  );
  const [tagsText, setTagsText] = useState((editing?.tags || []).join(", "));
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: tagsText.split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (editing) {
        await api.put(`/admin/blog/${editing.id}`, payload);
        toast.success("Post updated");
      } else {
        await api.post("/admin/blog", payload);
        toast.success("Post created");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[var(--ink-forest)]/40 backdrop-blur-sm">
      <div className="bg-[var(--bg-surface)] rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-[var(--border)] flex justify-between items-center sticky top-0 bg-[var(--bg-surface)] z-10">
          <h3 className="font-serif text-2xl">{editing ? "Edit post" : "New post"}</h3>
          <button onClick={onClose} className="p-2 hover:bg-[var(--muted)] rounded-full"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Title">
              <input required value={form.title} onChange={(e) => update("title", e.target.value)} className="ck-input" />
            </Field>
            <Field label="Slug">
              <input
                required
                value={form.slug}
                onChange={(e) => update("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
                className="ck-input"
              />
            </Field>
          </div>
          <Field label="Excerpt (1-2 sentences shown on listing)">
            <textarea required rows={2} value={form.excerpt} onChange={(e) => update("excerpt", e.target.value)} className="ck-input" />
          </Field>
          <Field label="Content (use blank lines between paragraphs)">
            <textarea required rows={10} value={form.content} onChange={(e) => update("content", e.target.value)} className="ck-input" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Cover image" full>
              <ImageUpload
                value={form.cover_image}
                onChange={(url) => update("cover_image", url)}
                label="Cover image"
                testid="blog-image-upload"
              />
            </Field>
            <Field label="Author">
              <input value={form.author} onChange={(e) => update("author", e.target.value)} className="ck-input" />
            </Field>
            <Field label="Category">
              <select value={form.category} onChange={(e) => update("category", e.target.value)} className="ck-input">
                <option value="beginner-guides">Beginner Guides</option>
                <option value="safety">Safety</option>
                <option value="education">Education</option>
                <option value="our-story">Our Story</option>
                <option value="general">General</option>
              </select>
            </Field>
            <Field label="Tags (comma separated)">
              <input value={tagsText} onChange={(e) => setTagsText(e.target.value)} className="ck-input" />
            </Field>
          </div>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.featured} onChange={(e) => update("featured", e.target.checked)} />
              Featured on homepage
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.published} onChange={(e) => update("published", e.target.checked)} />
              Published
            </label>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} className="flex-1 bg-[var(--terracotta)] text-white rounded-full py-3 text-sm font-semibold hover:bg-[var(--terracotta-deep)] disabled:opacity-60">
              {saving ? "Saving…" : "Save post"}
            </button>
            <button type="button" onClick={onClose} className="px-5 border border-[var(--border)] rounded-full text-sm hover:bg-[var(--muted)]">Cancel</button>
          </div>
        </form>

        <style>{`
          .ck-input {
            width: 100%;
            background: var(--bg-cream);
            border: 1px solid var(--border);
            border-radius: 12px;
            padding: 0.7rem 1rem;
            font-size: 0.9rem;
            color: var(--ink-forest);
            outline: none;
            font-family: inherit;
          }
          .ck-input:focus { border-color: var(--terracotta); }
        `}</style>
      </div>
    </div>
  );
}

function Subscribers() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/newsletter").then((r) => setSubs(r.data)).finally(() => setLoading(false));
  }, []);

  const exportCsv = () => {
    const csv = "email,source,created_at\n" + subs.map((s) => `${s.email},${s.source},${s.created_at}`).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  if (loading) return <div className="h-32 bg-[var(--muted)] rounded-2xl animate-pulse" />;

  return (
    <div data-testid="admin-subscribers">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-serif text-2xl text-[var(--ink-forest)]">{subs.length} subscribers</h2>
        {subs.length > 0 && (
          <button onClick={exportCsv} className="bg-[var(--ink-forest)] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[var(--ink-forest)]/90">
            Export CSV
          </button>
        )}
      </div>

      {subs.length === 0 ? (
        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-2xl p-10 text-center">
          <Mail className="w-10 h-10 text-[var(--sage)] mx-auto mb-4" />
          <p className="text-[var(--muted-fg)]">No subscribers yet. The popup appears 22 seconds after a new visitor arrives, or when they scroll 60% down the page.</p>
        </div>
      ) : (
        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--muted)]/50 text-left">
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3 hidden md:table-cell">Source</th>
                <th className="px-4 py-3 hidden md:table-cell">Joined</th>
              </tr>
            </thead>
            <tbody>
              {subs.map((s) => (
                <tr key={s.id} className="border-t border-[var(--border)]">
                  <td className="px-4 py-3 font-medium">{s.email}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-[var(--muted-fg)]">{s.source}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-[var(--muted-fg)]">
                    {new Date(s.created_at).toLocaleDateString("en-US", { dateStyle: "medium" })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}



function Coupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // coupon or "new"

  /* eslint-disable react-hooks/exhaustive-deps */
  const load = () => {
    setLoading(true);
    api
      .get("/admin/coupons")
      .then((r) => setCoupons(r.data))
      .catch(() => setCoupons([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  const del = async (c) => {
    if (!confirm(`Delete coupon ${c.code}? Past orders that used it will still keep their record.`)) return;
    try {
      await api.delete(`/admin/coupons/${c.id}`);
      toast.success(`Deleted ${c.code}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-coupons">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-serif text-2xl text-[var(--ink-forest)]">Coupons</h2>
        <button
          onClick={() => setEditing("new")}
          className="bg-[var(--terracotta)] text-white rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-1.5"
          data-testid="add-coupon-btn"
        >
          <Plus className="w-4 h-4" /> New coupon
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-16 bg-[var(--muted)] rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : coupons.length === 0 ? (
        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-2xl p-10 text-center">
          <Tag className="w-10 h-10 text-[var(--sage)] mx-auto mb-4" />
          <p className="text-[var(--muted-fg)]">No coupons yet. Click &ldquo;New coupon&rdquo; above to create one.</p>
        </div>
      ) : (
        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--muted)]/50 text-left">
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3 hidden sm:table-cell">Discount</th>
                <th className="px-4 py-3 hidden md:table-cell">Limits</th>
                <th className="px-4 py-3">Uses</th>
                <th className="px-4 py-3">Active</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {coupons.map((c) => (
                <tr key={c.id} className="border-t border-[var(--border)]" data-testid={`coupon-row-${c.code}`}>
                  <td className="px-4 py-3">
                    <span className="font-mono font-semibold text-[var(--terracotta)]">{c.code}</span>
                    {c.description && (
                      <p className="text-xs text-[var(--muted-fg)] mt-0.5 line-clamp-1">{c.description}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">{describeDiscount(c)}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-xs text-[var(--muted-fg)]">{describeLimits(c)}</td>
                  <td className="px-4 py-3 text-[var(--muted-fg)]">
                    {c.uses_count || 0}
                    {c.max_uses ? ` / ${c.max_uses}` : ""}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.active ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
                      {c.active ? "Active" : "Off"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => setEditing(c)}
                      className="p-1.5 hover:bg-[var(--muted)] rounded mr-1"
                      data-testid={`edit-coupon-${c.code}`}
                      aria-label="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => del(c)}
                      className="p-1.5 hover:bg-rose-50 text-rose-700 rounded"
                      data-testid={`delete-coupon-${c.code}`}
                      aria-label="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <CouponForm
          coupon={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function describeDiscount(c) {
  if (c.discount_type === "percent") return `${c.discount_value}% off`;
  if (c.discount_type === "fixed") return `${formatPrice(c.discount_value)} off`;
  if (c.discount_type === "free_shipping") return "Free shipping";
  return c.discount_type;
}

function describeLimits(c) {
  const parts = [];
  if (c.min_order) parts.push(`Min ${formatPrice(c.min_order)}`);
  if (c.max_uses) parts.push(`Max ${c.max_uses} uses`);
  if (c.expires_at) {
    const d = new Date(c.expires_at);
    if (!isNaN(d)) parts.push(`Expires ${d.toLocaleDateString("en-US", { dateStyle: "medium" })}`);
  }
  return parts.join(" · ") || "No limits";
}

function CouponForm({ coupon, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    code: coupon?.code || "",
    discount_type: coupon?.discount_type || "percent",
    discount_value: coupon?.discount_value ?? 10,
    min_order: coupon?.min_order ?? 0,
    max_uses: coupon?.max_uses ?? "",
    expires_at: coupon?.expires_at ? coupon.expires_at.slice(0, 10) : "",
    active: coupon?.active ?? true,
    description: coupon?.description || "",
  }));
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        discount_type: form.discount_type,
        discount_value: Number(form.discount_value) || 0,
        min_order: Number(form.min_order) || 0,
        max_uses: form.max_uses === "" ? null : Number(form.max_uses),
        expires_at: form.expires_at ? new Date(form.expires_at + "T23:59:59Z").toISOString() : null,
        active: form.active,
        description: form.description,
      };
      if (coupon) {
        await api.put(`/admin/coupons/${coupon.id}`, payload);
        toast.success(`Updated ${payload.code}`);
      } else {
        await api.post("/admin/coupons", payload);
        toast.success(`Created ${payload.code}`);
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-5 bg-[var(--ink-forest)]/40 backdrop-blur-sm" data-testid="coupon-form-modal">
      <form
        onSubmit={save}
        className="bg-[var(--bg-surface)] rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <div className="flex justify-between items-center p-6 border-b border-[var(--border)] sticky top-0 bg-[var(--bg-surface)]">
          <h3 className="font-serif text-2xl text-[var(--ink-forest)]">
            {coupon ? "Edit coupon" : "New coupon"}
          </h3>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-[var(--muted)]" data-testid="coupon-form-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <CField label="Code" required>
            <input
              type="text"
              required
              value={form.code}
              onChange={(e) => update("code", e.target.value.toUpperCase())}
              placeholder="WELCOME10"
              className="ck-input uppercase tracking-wider font-mono"
              data-testid="coupon-code-input"
            />
          </CField>

          <CField label="Discount type" required>
            <select
              value={form.discount_type}
              onChange={(e) => update("discount_type", e.target.value)}
              className="ck-input"
              data-testid="coupon-type-select"
            >
              <option value="percent">Percentage off</option>
              <option value="fixed">Fixed amount off</option>
              <option value="free_shipping">Free shipping</option>
            </select>
          </CField>

          {form.discount_type !== "free_shipping" && (
            <CField label={form.discount_type === "percent" ? "Percent (1–100)" : "Amount in $"} required>
              <input
                type="number"
                required
                min="0"
                step={form.discount_type === "percent" ? "1" : "0.01"}
                value={form.discount_value}
                onChange={(e) => update("discount_value", e.target.value)}
                className="ck-input"
                data-testid="coupon-value-input"
              />
            </CField>
          )}

          <CField label="Minimum order amount ($, 0 = none)">
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.min_order}
              onChange={(e) => update("min_order", e.target.value)}
              className="ck-input"
              data-testid="coupon-min-order-input"
            />
          </CField>

          <CField label="Max uses total (blank = unlimited)">
            <input
              type="number"
              min="1"
              value={form.max_uses}
              onChange={(e) => update("max_uses", e.target.value)}
              className="ck-input"
              data-testid="coupon-max-uses-input"
            />
          </CField>

          <CField label="Expires on (blank = never)">
            <input
              type="date"
              value={form.expires_at}
              onChange={(e) => update("expires_at", e.target.value)}
              className="ck-input"
              data-testid="coupon-expires-input"
            />
          </CField>

          <CField label="Description (shown to customers)">
            <input
              type="text"
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              placeholder="e.g. 10% off your first order"
              className="ck-input"
              data-testid="coupon-description-input"
            />
          </CField>

          <label className="flex items-center gap-2 text-sm text-[var(--ink-forest)]">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => update("active", e.target.checked)}
              data-testid="coupon-active-checkbox"
            />
            Active (customers can use this code)
          </label>
        </div>

        <div className="p-6 border-t border-[var(--border)] flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-full text-sm border border-[var(--border)] hover:bg-[var(--muted)]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-5 py-2.5 rounded-full text-sm bg-[var(--terracotta)] text-white font-semibold hover:bg-[var(--terracotta-deep)] disabled:opacity-60"
            data-testid="coupon-save-btn"
          >
            {saving ? "Saving…" : coupon ? "Save changes" : "Create coupon"}
          </button>
        </div>
      </form>

      <style>{`
        .ck-input {
          width: 100%;
          background: var(--bg-cream);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 0.75rem 1rem;
          font-size: 0.9rem;
          color: var(--ink-forest);
          outline: none;
        }
        .ck-input:focus { border-color: var(--terracotta); }
      `}</style>
    </div>
  );
}

function CField({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium uppercase tracking-wide text-[var(--ink-forest)] block mb-1.5">
        {label} {required && <span className="text-[var(--terracotta)]">*</span>}
      </span>
      {children}
    </label>
  );
}
